import { Authenticated } from "components/Authenticated";
import { Spinner } from "components/Spinner";
import { useDebouncedValue } from "core/hooks/useDebouncedValue";
import Header from "layout/header";
import { LayoutConstants } from "layout/LayoutConstants";
import LeftMenu from "layout/left-menu";
import { DrawerOpenState } from "layout/left-menu/types";
import Notifications from "layout/Notifications";
import { PageRoutes } from "pages/PageRoutes";
import { useState } from "react";
import { useToggle } from "react-use";

import MenuIcon from "@mui/icons-material/Menu";
import { styled, useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";

const Container = styled(Box)({
  flex: "1 1 1px",

  marginTop: `${LayoutConstants.titleBar.height}px`,
  overflow: "auto",

  display: "flex",
  flexFlow: "row-reverse wrap",
  justifyContent: "space-between",
  alignItems: "flex-start",
  alignContent: "flex-start"
});

const LeftColumn = styled("div")(({ theme }) => ({
  flex: "1 1 700px",
  height: "inherit",

  [theme.breakpoints.up("md")]: {
    minWidth: "700px"
  }
}));

const Layout = () => {
  const [menuClicked, toggleMenuClicked] = useToggle(false);
  const [isHoveringMenu, setIsHoveringMenu] = useState(false);
  const isHoveringMenuDebounced = useDebouncedValue(isHoveringMenu, 200);
  const handleMouseEnter = () => setIsHoveringMenu(true);
  const handleMouseLeave = () => setIsHoveringMenu(false);
  const handleOpenToggle = () => menuClicked && toggleMenuClicked();

  const theme = useTheme();
  const showModal = useMediaQuery(theme.breakpoints.down("md"));

  // When hovering over the menu, open it after a short delay.
  // When the user stops hovering over the menu, close it immediately.
  const getOpenState = () => {
    if (menuClicked) {
      return DrawerOpenState.Open;
    }

    return !showModal || (isHoveringMenu && isHoveringMenuDebounced)
      ? DrawerOpenState.Open
      : DrawerOpenState.Closed;
  };

  return (
    <Authenticated
      renderSpinner={spinnerMessage => (
        <Spinner variant="fullscreen">{spinnerMessage}</Spinner>
      )}
    >
      <Box display="flex" sx={{ background: theme.palette.background.default }}>
        <Header>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleMenuClicked}
          >
            <MenuIcon />
          </IconButton>
        </Header>

        <LeftMenu
          openState={getOpenState()}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onOpenToggle={handleOpenToggle}
        />

        <Container component="main">
          <LeftColumn>
            <PageRoutes />
          </LeftColumn>
        </Container>

        <Notifications />
      </Box>
    </Authenticated>
  );
};
// eslint-disable-next-line import/no-default-export
export default Layout;
